import { ChakraBox, DateFormat, FireflyKebabMenuItem, TagColor } from '@equip.health/ui';
import CareTeamMemberCard from '../CareTeamMemberCard';
import { getTextForProviderAssignmentCard } from '~/lib/util/careTeam.util';
import { Provider } from '~/lib/types/careTeam/careTeam';
import { CareTeamTranslations } from '~/lib/constants/careTeam.constants';
import { UserStatus } from '~/lib/constants/user.constants';
import { formattedDateFromISO } from '~/lib/util/date.util';

type CareTeamProviderListProps = {
  hasTreatmentEpisode?: boolean;
  patientId?: string;
  providers: Provider[];
  showTextContent?: boolean;
  showTags?: boolean;
  getKebabMenuOptions?: (provider: Provider) => FireflyKebabMenuItem[] | undefined;
};

const getStatusTag = (
  provider: Provider,
): { color: TagColor; id: string; value: string } | undefined => {
  if (provider.userStatus === UserStatus.INACTIVE) return undefined;
  if (provider.isIncomingReassignment) {
    return {
      color: TagColor.marigold,
      id: `care-team-member__user-pending-tag-${provider.id}`,
      value: CareTeamTranslations.PENDING,
    };
  }
  if (provider.isOutgoingReassignment && provider.reassignmentFailureReason) {
    return {
      color: TagColor.red,
      id: `care-team-member__user-failure-tag-${provider.id}`,
      value: CareTeamTranslations.REASSIGNMENT_FAILED,
    };
  }
  return undefined;
};

const getReassignmentTextContent = (provider: Provider): string | null => {
  if (provider.isIncomingReassignment && provider.reassignmentDate) {
    return `Starting ${formattedDateFromISO(provider.reassignmentDate, DateFormat.shortMonthDayYear)}`;
  }
  if (provider.isOutgoingReassignment && provider.reassignmentDate) {
    return `Ending ${formattedDateFromISO(provider.reassignmentDate, DateFormat.shortMonthDayYear)}`;
  }
  return null;
};

const CareTeamProviderList = ({
  hasTreatmentEpisode = false,
  patientId,
  providers = [],
  showTextContent = false,
  showTags = false,
  getKebabMenuOptions,
}: CareTeamProviderListProps) => {
  return (
    <>
      {providers.map((provider) => {
        const reassignmentText = getReassignmentTextContent(provider);
        let textContent: string | null = reassignmentText;

        if (!textContent && showTextContent) {
          if (hasTreatmentEpisode) {
            textContent = getTextForProviderAssignmentCard(
              provider.startDate,
              provider.endDate,
            );
          } else {
            textContent = CareTeamTranslations.INQUIRY_TEXT_CONTENT;
          }
        }

        const key = provider.isIncomingReassignment
          ? `${provider.id}-reassignment-${provider.reassignmentId}`
          : provider.id;

        return (
          <ChakraBox key={key} marginBottom={4}>
            <CareTeamMemberCard
              {...provider}
              kebabMenuOptions={getKebabMenuOptions?.(provider)}
              patientId={patientId}
              showTags={showTags}
              statusTag={getStatusTag(provider)}
              textContent={textContent}
            />
          </ChakraBox>
        );
      })}
    </>
  );
};

export default CareTeamProviderList;
